import React, { useEffect, useRef, useState } from 'react';
import {
  Send24Regular,
  Warning24Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  DocumentText20Regular,
  Attach24Regular,
  Dismiss20Regular,
  Edit20Regular,
  Checkmark20Regular,
} from '@fluentui/react-icons';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { useAppStore } from '@/stores/chatbotStore';
import { Message, SourceDocument } from '@/types/api';
import DocumentManager from '@/components/DocumentManager';

/* ------------------------------------------------------------------ */
/*  SourceCard                                                        */
/* ------------------------------------------------------------------ */

const SourceCard: React.FC<{ source: SourceDocument; index: number }> = ({ source, index }) => {
  const [expanded, setExpanded] = useState(false);

  const scoreColor = () => {
    if (source.score == null) return null;
    if (source.score >= 0.7) return { bg: '#dff6dd', fg: '#107c10' };
    if (source.score >= 0.4) return { bg: '#fff4ce', fg: '#797600' };
    return { bg: '#fed9cc', fg: '#d83b01' };
  };

  const sc = scoreColor();

  return (
    <div className="source-card" style={{ flexDirection: 'column', alignItems: 'stretch', padding: '10px 12px', cursor: 'default' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="source-number">{index + 1}</span>
          <strong style={{ fontSize: 12 }}>{source.filename}</strong>
          {source.page != null && (
            <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12 }}> - p.{source.page}</span>
          )}
        </span>
        {sc && (
          <span style={{ background: sc.bg, color: sc.fg, padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
            {(source.score! * 100).toFixed(0)}%
          </span>
        )}
      </div>

      <p style={{
        color: '#6b7280',
        fontSize: 12,
        lineHeight: 1.4,
        whiteSpace: 'pre-wrap',
        margin: '6px 0 0',
        ...(!expanded && {
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical' as never,
          overflow: 'hidden',
        }),
      }}>
        {source.text_excerpt}
      </p>

      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--color-primary)', fontSize: 11, fontWeight: 600,
          padding: '4px 0 0', textAlign: 'left',
        }}
      >
        {expanded ? 'Voir moins' : 'Voir plus'}
      </button>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  SourcesBubble                                                     */
/* ------------------------------------------------------------------ */

const SourcesBubble: React.FC<{ sources: SourceDocument[] }> = ({ sources }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="sources-bubble">
      <button
        className="sources-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '10px 14px', background: 'none', border: 'none',
          cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#6b7280',
          width: '100%', textAlign: 'left',
        }}
      >
        {isExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        <DocumentText20Regular />
        {sources.length} source{sources.length > 1 ? 's' : ''} utilisee{sources.length > 1 ? 's' : ''}
      </button>

      {isExpanded && (
        <div className="sources-list" style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sources.map((source, index) => (
            <SourceCard key={source.node_id || index} source={source} index={index} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  MessageItem                                                       */
/* ------------------------------------------------------------------ */

interface MessageItemProps {
  message: Message;
  isLatest?: boolean;
  isStreaming?: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({ message, isLatest, isStreaming }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`message ${isUser ? 'message-user' : 'message-assistant'}`}>
      <div className="message-content">
        {isUser ? (
          <p>{message.content}</p>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              code({ node, inline, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={oneLight}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {message.content}
          </ReactMarkdown>
        )}
      </div>

      {/* Source attribution for assistant messages */}
      {!isUser && message.source_documents && message.source_documents.length > 0 && !isStreaming && (
        <SourcesBubble sources={message.source_documents} />
      )}

      {/* Metadata row */}
      {message.response_time && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>
          {message.response_time.toFixed(1)}s
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  ThinkingIndicator                                                 */
/* ------------------------------------------------------------------ */

const ThinkingIndicator: React.FC = () => (
  <div className="thinking-indicator">
    <div className="dots">
      <span className="dot" />
      <span className="dot" />
      <span className="dot" />
    </div>
    <span>Reflexion en cours...</span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  WelcomeScreen                                                     */
/* ------------------------------------------------------------------ */

const SUGGESTIONS = [
  'Resume les points cles de mes documents',
  'Quels sont les sujets abordes ?',
  'Compare les informations entre les fichiers',
  'Explique-moi le contenu principal',
];

interface WelcomeScreenProps {
  onSuggestionClick: (text: string) => void;
  chatbotName?: string;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onSuggestionClick, chatbotName }) => (
  <div className="welcome-screen">
    <h1 className="welcome-title">Comment puis-je vous aider ?</h1>
    <p className="welcome-subtitle">
      {chatbotName
        ? `Posez une question sur les documents de ${chatbotName}.`
        : 'Selectionnez un chatbot pour commencer.'}
    </p>
    <div className="welcome-suggestions">
      {SUGGESTIONS.map((s) => (
        <button key={s} className="suggestion-chip" onClick={() => onSuggestionClick(s)}>
          {s}
        </button>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  ChatInterface (main)                                              */
/* ------------------------------------------------------------------ */

const ChatInterface: React.FC = () => {
  const {
    currentChatbot,
    currentConversation,
    messages,
    documents,
    isStreaming,
    isLoadingMessages,
    chatError,
    sendMessageStream,
    renameConversation,
    createConversation,
  } = useAppStore();

  const [input, setInput] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [showUploadPopover, setShowUploadPopover] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  /* ---- Auto-scroll ---- */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /* ---- Textarea auto-grow ---- */
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  };

  /* ---- Close upload popover on outside click ---- */
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't close if click is inside the popover
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      // Don't close if click is inside a Fluent UI Dialog portal
      if (target.closest('[role="dialog"]') || target.closest('.fui-DialogSurface')) return;
      setShowUploadPopover(false);
    };
    if (showUploadPopover) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUploadPopover]);

  /* ---- Focus title input when editing ---- */
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  /* ---- Send logic ---- */
  const handleSend = async () => {
    if (!input.trim() || isStreaming || !currentChatbot) return;
    const message = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      let convId = currentConversation?.id;
      if (!convId) {
        const conv = await createConversation(currentChatbot.id, message.slice(0, 50));
        convId = conv.id;
      }
      await sendMessageStream(convId, message);
    } catch (err) {
      console.error('Chat error:', err);
    } finally {
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /* ---- Title editing ---- */
  const startEditingTitle = () => {
    if (!currentConversation) return;
    setEditTitle(currentConversation.title);
    setIsEditingTitle(true);
  };

  const saveTitle = async () => {
    if (currentConversation && editTitle.trim()) {
      await renameConversation(currentConversation.id, editTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') saveTitle();
    if (e.key === 'Escape') setIsEditingTitle(false);
  };

  /* ---- Suggestion click ---- */
  const handleSuggestionClick = (text: string) => {
    setInput(text);
    textareaRef.current?.focus();
  };

  /* ---- Derived state ---- */
  const hasDocuments = documents.length > 0;
  const showThinking =
    isStreaming &&
    (messages.length === 0 || messages[messages.length - 1].role !== 'assistant');

  /* ---- Render ---- */
  return (
    <div className="chat-zone">

      {/* ---- Messages area ---- */}
      <div className="chat-messages">
        {isLoadingMessages ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <ThinkingIndicator />
          </div>
        ) : !currentChatbot ? (
          <WelcomeScreen onSuggestionClick={handleSuggestionClick} />
        ) : !hasDocuments ? (
          <div className="welcome-screen">
            <Warning24Regular style={{ fontSize: 40, color: 'var(--color-primary)' }} />
            <h1 className="welcome-title" style={{ fontSize: 20 }}>Aucun document</h1>
            <p className="welcome-subtitle">
              Uploadez des documents pour commencer a discuter avec {currentChatbot.name}.
            </p>
          </div>
        ) : messages.length === 0 && !currentConversation ? (
          <WelcomeScreen onSuggestionClick={handleSuggestionClick} chatbotName={currentChatbot.name} />
        ) : messages.length === 0 ? (
          <WelcomeScreen onSuggestionClick={handleSuggestionClick} chatbotName={currentChatbot.name} />
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageItem
                key={message.id}
                message={message}
                isLatest={index === messages.length - 1}
                isStreaming={isStreaming && index === messages.length - 1}
              />
            ))}

            {showThinking && <ThinkingIndicator />}

            <div ref={messagesEndRef} />
          </>
        )}

        {/* Error */}
        {chatError && (
          <div style={{
            background: '#fdf2f2', border: '1px solid #fca5a5', borderRadius: 10,
            padding: '12px 16px', color: '#991b1b', margin: '16px 0',
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 14,
          }}>
            <Warning24Regular />
            {chatError}
          </div>
        )}
      </div>

      {/* ---- Input area ---- */}
      <div className="chat-input-area">
        <div className="chat-input-wrapper">
          <textarea
            ref={textareaRef}
            className="chat-textarea"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              !currentChatbot
                ? 'Selectionnez un chatbot...'
                : !hasDocuments
                ? 'Uploadez des documents pour commencer'
                : 'Posez votre question...'
            }
            disabled={!currentChatbot || isStreaming}
            rows={1}
          />

          <div className="chat-input-actions">
            <div className="left-actions" style={{ position: 'relative' }}>
              {currentChatbot && (
                <>
                  <button
                    onClick={() => setShowUploadPopover(!showUploadPopover)}
                    title="Joindre un document"
                  >
                    <Attach24Regular />
                  </button>

                  {showUploadPopover && (
                    <div ref={popoverRef} className="upload-popover" style={{ width: 420, maxHeight: 500, overflowY: 'auto' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px 4px', borderBottom: '1px solid #e5e7eb', marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#1C1C1C' }}>Documents</span>
                        <button
                          onClick={() => setShowUploadPopover(false)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex' }}
                        >
                          <Dismiss20Regular />
                        </button>
                      </div>
                      <DocumentManager chatbotId={currentChatbot.id} />
                    </div>
                  )}
                </>
              )}

              {hasDocuments && (
                <span style={{ fontSize: 12, color: '#9ca3af', alignSelf: 'center', marginLeft: 4 }}>
                  {documents.length} doc{documents.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <button
              className="send-button"
              onClick={handleSend}
              disabled={!input.trim() || isStreaming || !currentChatbot || !hasDocuments}
            >
              <Send24Regular />
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
